'use client';

import { useCallback, useEffect, useRef } from 'react';

interface VideoDescriptionProps {
  description: string;
  onDataChange: (key: string, value: string) => void;
}

export default function VideoDescription({
  description,
  onDataChange,
  isEditable,
}: VideoDescriptionProps) {
  const contentRef = useRef<HTMLDivElement>(null);

  const styleURLs = useCallback((node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const regex = /(https?:\/\/[^\s]+)/g;
      const text = node.textContent || '';
      const matches = text.match(regex);

      if (matches) {
        const fragment = document.createDocumentFragment();
        let lastIndex = 0;

        matches.forEach((match) => {
          const index = text.indexOf(match, lastIndex);
          if (index > lastIndex) {
            fragment.appendChild(
              document.createTextNode(text.slice(lastIndex, index)),
            );
          }
          const span = document.createElement('span');
          span.textContent = match;
          if (match.startsWith('http')) {
            span.className = 'text-blue-500 cursor-pointer';
          }
          fragment.appendChild(span);
          lastIndex = index + match.length;
        });

        if (lastIndex < text.length) {
          fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
        }

        node.parentNode?.replaceChild(fragment, node);
      }
    } else if (
      node.nodeType === Node.ELEMENT_NODE &&
      node.nodeName !== 'SPAN'
    ) {
      Array.from(node.childNodes).forEach(styleURLs);
    }
  }, []);

  const handleInput = useCallback(() => {
    if (!contentRef.current) return;
    Array.from(contentRef.current.childNodes).forEach(styleURLs);
    const content = contentRef.current.innerHTML;
    onDataChange('description', content);
  }, [onDataChange, styleURLs]);

  useEffect(() => {
    if (contentRef.current && contentRef.current.innerText !== description) {
      contentRef.current.innerHTML = description || '';
      Array.from(contentRef.current.childNodes).forEach(styleURLs);
    }
  }, [description, styleURLs]);

  return (
    <div className="p-4">
      <div
        ref={contentRef}
        className={`outline-none`}
        contentEditable={isEditable}
        onInput={handleInput}
        suppressContentEditableWarning
      />
    </div>
  );
}
